import { useEffect, useState } from "react"
import { Link } from "react-router-dom"
import { getDashboard } from "../../api/api"
import { compactNumber, scoreTone } from "../../utils/seo"

export default function Dashboard() {
  const [stats, setStats] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState("")

  useEffect(() => {
    getDashboard()
      .then((res) => setStats(res.data))
      .catch((err) => setError(err.response?.data?.error || "Failed to load dashboard"))
      .finally(() => setLoading(false))
  }, [])

  if (loading) return <div className="page">Loading dashboard...</div>
  if (error) return <div className="page error">{error}</div>

  const latest = stats.latestReport
  const score = latest?.score || stats.score || 0
  const overview = latest?.insights?.domainOverview || {}
  const cards = [
    ["Projects", stats.projects, "Client websites being monitored"],
    ["Reports", stats.reports, "Saved audits in the system"],
    ["Pages", stats.pages, "Pages crawled in latest run"],
    ["Keywords", stats.keywords, "Discovered keyword signals"],
    ["Authority", overview.authorityScore || 0, "Latest authority proxy"],
    ["Visibility", `${overview.visibilityScore || 0}/100`, "Latest organic visibility proxy"]
  ]

  const issueCards = latest?.insights?.issueCards || []
  const roadmap = latest?.insights?.roadmap || []
  const displayValue = (value) => (typeof value === "number" ? compactNumber(value) : value)

  return (
    <div className="page">
      <section className="hero-panel">
        <div>
          <p className="eyebrow">Master SEO Engine Ultimate</p>
          <h1>SEO command center</h1>
          <p className="muted">
            Crawl, audit, prioritize fixes, generate AI content and track project health from one place.
          </p>
          <div className="actions">
            <Link className="button" to="/projects">Add project</Link>
            <Link className="button secondary" to="/audit">Run instant audit</Link>
          </div>
        </div>
        <div className={`score-ring ${scoreTone(score)}`} style={{ "--score": score }}>
          {score}
        </div>
      </section>

      <div className="grid">
        {cards.map(([label, value, helper]) => (
          <div className="metric-card" key={label}>
            <p className="metric-label">{label}</p>
            <div className="metric">{displayValue(value)}</div>
            <p className="muted">{helper}</p>
          </div>
        ))}
      </div>

      <div className="split-grid" style={{ marginTop: 18 }}>
        <div className="card">
          <h2>Priority issues</h2>
          {!latest && <div className="empty-state">Run an audit to generate prioritized SEO issues.</div>}
          {latest && issueCards.length === 0 && (
            <div className="empty-state">No major issues detected in the latest report.</div>
          )}
          <div className="insight-list">
            {issueCards.slice(0, 6).map((issue) => (
              <div className="insight-item" key={issue.key}>
                <div className="actions">
                  <span className={`pill ${issue.priority}`}>{issue.priority}</span>
                  <strong>{issue.title}</strong>
                  <span className="muted">{issue.count} found</span>
                </div>
                <p className="muted">{issue.impact}</p>
                <p>{issue.fix}</p>
              </div>
            ))}
          </div>
        </div>

        <div className="card">
          <h2>Fix roadmap</h2>
          {!latest && <div className="empty-state">Your action plan will appear after the first audit.</div>}
          {latest && roadmap.length === 0 && (
            <div className="empty-state">Latest audit looks clean. Keep monitoring after new content changes.</div>
          )}
          <div className="roadmap">
            {roadmap.map((item) => (
              <div className="roadmap-item" key={item.step}>
                <div>
                  <strong>{item.title}</strong>
                  <p className="muted">{item.action}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  )
}
